import * as React from 'react';
import * as styles from './PromoSnippetLayout.css';
import { MIDDLE_DOT, NO_BREAK_SPACE } from '../../constants/symbolsUnicode';
import { UIHeading4 } from '@cian/ui-kit';
import { replaceSpacesToNbsp } from '../../utils/text/replaceSpacesToNbsp';

interface IPromoSnippetLayoutProps {
  preview: React.ReactNode;
  labels: React.ReactNode;
  title: React.ReactNode;
  reviews: React.ReactNode;
  underground: React.ReactNode;
  address: React.ReactNode;
  advertises: React.ReactNode;
  callButton: React.ReactNode;
  messageButton?: React.ReactNode;
  favoriteButton: React.ReactNode;
  builder: React.ReactNode;
  roomCounts: React.ReactNode;
  specialStatusDisplay: string;
  offerTypes?: string | null;
  firstMaterial?: string | null;
  onHover(hovered: boolean): void;
  url: string;
  onClick(): void;
}

export function PromoSnippetLayout({
  preview,
  reviews,
  title,
  labels,
  specialStatusDisplay,
  firstMaterial,
  address,
  underground,
  callButton,
  messageButton,
  favoriteButton,
  roomCounts,
  advertises,
  offerTypes,
  onHover,
  onClick,
  url,
  builder,
}: IPromoSnippetLayoutProps) {
  const handleMouseEnter = React.useCallback(() => {
    onHover(true);
  }, [onHover]);

  const handleMouseLeave = React.useCallback(() => {
    onHover(false);
  }, [onHover]);

  const facts = React.useMemo(() => {
    let result = replaceSpacesToNbsp(specialStatusDisplay);
    if (offerTypes) {
      result += `${NO_BREAK_SPACE}${MIDDLE_DOT} ${replaceSpacesToNbsp(offerTypes)}`;
    }
    if (firstMaterial) {
      result += `${NO_BREAK_SPACE}${MIDDLE_DOT} ${replaceSpacesToNbsp(firstMaterial)}`;
    }

    return result;
  }, [specialStatusDisplay, offerTypes, firstMaterial]);

  return (
    <div
      className={styles['container']}
      data-testid="PromoSnippet"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <a href={url} onClick={onClick} target="_blank" className={styles['click-area']} rel="noreferrer" />
      <div className={styles['preview']}>{preview}</div>
      <div className={styles['content']}>
        <div className={styles['content-inner']}>
          <div className={styles['title']}>{title}</div>
          <div className={styles['header']}>
            <UIHeading4>{facts}</UIHeading4>
            {reviews && (
              <div data-testid="promo_snippet_reviews" className={styles['reviews']}>
                {reviews}
              </div>
            )}
            {labels && <div className={styles['labels']}>{labels}</div>}
          </div>
          <div className={styles['underground']}>{underground}</div>
          <div className={styles['address']}>{address}</div>
          {advertises && <div className={styles['advertises']}>{advertises}</div>}
        </div>

        <div className={styles['footer']}>
          <div className={styles['builder']}>{builder}</div>
          <div className={styles['contacts']}>
            <div className={styles['buttons']}>
              <div className={styles['call']}>{callButton}</div>
              <div className={styles['message']}>{messageButton}</div>
            </div>
            <div className={styles['rooms']}>{roomCounts}</div>
          </div>
        </div>
      </div>
      <div className={styles['favorite']}>{favoriteButton}</div>
    </div>
  );
}
